import React, { useEffect, useRef, useState } from 'react'
import { Modal, ModalContent } from 'shared/components/common/modal'
import { t } from 'src/translations'
import { RECOVERY_REASONS, Reason } from '../lib/reasons'
import RecoveryModalHeader from './RecoveryModalHeader'
import RecoveryModalFooter from './RecoveryModalFooter'
import RecoveryChoiceRow from './RecoveryChoiceRow'
import useRovingRadioGroup from './useRovingRadioGroup'

interface Props {
  isOpen: boolean
  onClose: () => void
  // Fired when the guest picks a leaf reason, which carries both the Intercom
  // 'Order support selection' value and the route the flow takes next.
  onSelectReason: (reason: Reason) => void
}

// D1 "What happened?" — radio rows + a Next CTA, disabled until one is chosen. A 'subMenu' reason
// drills into a follow-up list; a leaf reason goes to onSelectReason on Next.
export const IssueSelectionModal = ({
  isOpen,
  onClose,
  onSelectReason
}: Props) => {
  // Menu stack: [] = top "What happened?" list; pushing a subMenu parent drills in.
  const [menuStack, setMenuStack] = useState<Reason[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)

  // Reset to the top on each open, so no drill-down or selection carries into a new session.
  useEffect(() => {
    if (isOpen) {
      setMenuStack([])
      setSelectedId(null)
    }
  }, [isOpen])

  const parent = menuStack[menuStack.length - 1]
  const options = parent?.children ?? RECOVERY_REASONS
  const isSubMenu = menuStack.length > 0
  const selected = options.find((r) => r.id === selectedId) ?? null

  // Drilling in (or back) swaps the whole list in place, and the focus trap only takes focus once,
  // on mount — so without this the change of screen is silent. Focus on open is the trap's job; it
  // targets this same heading via ariaLabelledBy, hence the tabIndex={-1}.
  const depth = menuStack.length
  const previousDepth = useRef(depth)
  useEffect(() => {
    if (previousDepth.current !== depth) {
      previousDepth.current = depth
      titleRef.current?.focus()
    }
  }, [depth])

  // Single tab stop + arrow keys over the rows, as a radio group is expected to behave.
  const rowProps = useRovingRadioGroup(options, selected, (reason: Reason) =>
    setSelectedId(reason.id)
  )

  const goBack = () => {
    setMenuStack((s) => s.slice(0, -1))
    setSelectedId(null)
  }

  const next = () => {
    if (!selected) return
    if (selected.route === 'subMenu') {
      // Drill into the sub-menu; the leaf reason resolves on the next Next.
      setMenuStack((s) => [...s, selected])
      setSelectedId(null)
      return
    }
    onSelectReason(selected)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId='recovery-issue-modal'
      className='refundSupportModal'
    >
      <ModalContent
        wrapperClassName='refundSupportWrapper'
        contentClassName='refundResolutionModal'
        ariaLabelledBy='recovery-issue-title'
        ariaDescribedBy='recovery-issue-subtitle'
      >
        <RecoveryModalHeader onBack={isSubMenu ? goBack : undefined} />
        <div className='refundResolutionBody'>
          <h2
            ref={titleRef}
            id='recovery-issue-title'
            className='title'
            tabIndex={-1}
          >
            {parent
              ? t(`${parent.i18nKey}.title`)
              : t('order-confirmation.recovery.issue.title')}
          </h2>
          <p id='recovery-issue-subtitle' className='subtitle'>
            {isSubMenu
              ? t('order-confirmation.recovery.sub-issue.subtitle')
              : t('order-confirmation.recovery.issue.subtitle')}
          </p>

          <div
            role='radiogroup'
            aria-labelledby='recovery-issue-title'
            className='resolutionOptions'
          >
            {options.map((reason, index) => (
              <RecoveryChoiceRow
                key={reason.id}
                role='radio'
                isSelected={selectedId === reason.id}
                className='recoveryReason'
                testId={`recovery-reason-${reason.id}`}
                {...rowProps(reason, index)}
              >
                <span className='optionTitle'>
                  {t(`${reason.i18nKey}.title`)}
                </span>
                <span className='detail'>
                  {t(`${reason.i18nKey}.description`)}
                </span>
              </RecoveryChoiceRow>
            ))}
          </div>
        </div>
        <RecoveryModalFooter>
          <button
            type='button'
            className='resolutionCta'
            data-testid='recovery-issue-next'
            disabled={!selected}
            onClick={next}
          >
            {selected
              ? t('order-confirmation.recovery.next')
              : t('order-confirmation.recovery.select-prompt')}
          </button>
        </RecoveryModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default IssueSelectionModal
